import React, { FC, Fragment, useContext, useEffect, useState } from 'react';
import { Box, Grid, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Organization } from 'models/organization';
import { Dentist, DentistType, User } from 'models/user';
import { useLazyQuery, useMutation } from '@apollo/client';
import { getClinicTemplate } from 'apollo/templates/organization/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import AddIcon from '@mui/icons-material/Add';
import { LabOrderContext } from 'pages/order-flow/lab-order/lab-order-context';
import AddDentistToClinicDialog from 'components/shared/add-dentist-to-clinic-dialog/add-dentist-to-clinic-dialog.component';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import DentistsWithClinicSearch from './dentist-with-clinic-search/dentists-with-clinic-search.component';
import DeliveryClinicAutocomplete from './delivery-clinic-autocomplete/delivery-clinic-autocomplete.component';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import { useAssignInternalDentistId } from 'hooks/useAssignInternalDentistId';
import { setLabDentistDefaultClinicTemplate } from 'apollo/templates/user';
import ConfirmDialog from 'components/shared/confirm-dialog/confirm-dialog.component';
import { getLabDefaultClinicId } from 'utils/lab-dentist-default-clinic';
import { OrderInputType } from 'models/order-input/shared';

const DentistsWithClinic: FC = () => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const { order, setOrder, setIsTioTandvardPricingEnabled } = useContext(LabOrderContext);
    const [selectedClinic, setSelectedClinic] = useState<Partial<Organization> | null>(null);
    const [selectedDeliveryClinic, setSelectedDeliveryClinic] = useState<Organization | null>(null);
    const [selectedDentist, setSelectedDentist] = useState<Dentist | null>(null);
    const [addDentistDialogOpen, setAddDentistDialogOpen] = useState(false);
    const [clinicPendingSaveAsDefault, setClinicPendingSaveAsDefault] = useState<Organization | null>(null);
    const { showBoundary } = useDenthubErrorBoundary();
    const currentOrg = currentOrgVar();
    const { assignIdToDentist } = useAssignInternalDentistId();

    useEffect(() => {
        if (order.dentist && order.customer && order.deliveryDetails) {
            // Convert from User to Dentist type
            const fullInfo = `${order.dentist.name?.firstName} ${order.dentist.name?.lastName} - ${order.customer.name}`;
            const dentist = {
                ...order.dentist,
                organization: order.customer,
                fullNameWithClinicInfo: fullInfo,
                type: order.dentist.local ? DentistType.MY_DENTIST : DentistType.DENTHUB_CONNECTED_DENTIST,
            } as Dentist;
            setSelectedDentist(dentist);
            setSelectedClinic(order.customer);
            // Picking a new dentist resets pickUpLocation to `{}`, which is truthy, so only
            // treat it as a selection when it carries a real id - otherwise clear it and let
            // the delivery clinic dropdown prefill the dentist's default clinic.
            setSelectedDeliveryClinic(
                order.deliveryDetails.pickUpLocation?._id ? order.deliveryDetails.pickUpLocation : null,
            );
        }
    }, [order.customer, order.dentist, order.deliveryDetails.pickUpLocation]);

    const [getClinic] = useLazyQuery(getClinicTemplate, {
        onError: (err) => {
            showBoundary(err);
        },
        onCompleted: async ({ clinic }) => {
            setSelectedClinic(clinic);
            const isTioTandvardPricingEnabled = customerSettings.getCustomerSetting(
                clinic.parentClinicId ? clinic.parentClinic.customerSettings : clinic.customerSettings,
                CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING,
            );
            const isTioTandvardPricingForLabEnabled = customerSettings.getCustomerSetting(
                currentOrg.customerSettings,
                CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING_FOR_LAB,
            );
            // orderVersion is the backend's resolved answer - parent-clinic inheritance and the
            // local-clinic rule are already applied - so read it instead of re-deriving it from
            // the customer settings here.
            const isActionBasedClinic = clinic.orderVersion === CONSTANTS.SETTINGS_VALUES.ACTION_BASED;
            setIsTioTandvardPricingEnabled(
                isActionBasedClinic ? isTioTandvardPricingEnabled : isTioTandvardPricingForLabEnabled,
            );
            setOrder((prev) => {
                const updatedOrder = {
                    ...prev,
                    // Single writer of order.customer. clinic._id and isActionBasedOrder have to
                    // land in the same update: _id is what triggers the construction step to
                    // fetch a price list, and isActionBasedOrder is what picks which one.
                    customer: clinic,
                    isActionBasedOrder: isActionBasedClinic,
                };
                if (clinic.local) {
                    updatedOrder.deliveryDetails = {
                        ...prev.deliveryDetails,
                        pickUpLocation: clinic,
                        destination: clinic,
                    };
                }
                return updatedOrder;
            });
        },
    });

    const [setLabDentistDefaultClinic] = useMutation(setLabDentistDefaultClinicTemplate, {
        onError: (error) => {
            showBoundary(error);
        },
    });

    const showDeliveryClinicDropdown = () => {
        return true;
    };

    const labDefaultClinicId = getLabDefaultClinicId(selectedDentist, selectedClinic?._id);

    /**
     * Ask to store the picked child clinic only when the lab actively picked a child
     * clinic for a dentist that has no default yet, while creating a new order.
     */
    const shouldOfferToSaveAsDefault = (deliveryClinic: Organization, isUserSelection: boolean) => {
        return (
            isUserSelection &&
            order.inputType === OrderInputType.NEW &&
            !labDefaultClinicId &&
            !!selectedClinic?._id &&
            !!selectedDentist?._id &&
            deliveryClinic._id !== selectedClinic._id
        );
    };

    const handleSaveAsDefaultConfirm = async () => {
        const clinicToSave = clinicPendingSaveAsDefault;
        if (!clinicToSave || !selectedDentist?._id || !selectedClinic?._id) {
            setClinicPendingSaveAsDefault(null);
            return;
        }
        const mainClinicId = selectedClinic._id;
        await setLabDentistDefaultClinic({
            variables: {
                dentistId: selectedDentist._id,
                mainClinic: mainClinicId,
                defaultClinic: clinicToSave._id,
            },
        });
        setClinicPendingSaveAsDefault(null);
        // Keep the order's dentist in sync so the prompt is not shown again for this dentist.
        setOrder((prev) => ({
            ...prev,
            dentist: {
                ...prev.dentist,
                labDentistDefaultClinics: [
                    ...(prev.dentist?.labDentistDefaultClinics || []),
                    { mainClinic: mainClinicId, defaultClinic: clinicToSave },
                ],
            } as User,
        }));
    };

    const handleClinicChange = async (selectedClinic: Organization) => {
        await getClinic({
            variables: {
                id: selectedClinic._id,
            },
        });
    };

    const handleDentistChange = async (dentist: Dentist) => {
        if (dentist.clinic && !isSelectedDentist(dentist)) {
            setSelectedClinic(null);
            setSelectedDeliveryClinic(null);
            const newClinic = dentist.clinic as Organization;
            setSelectedDentist(dentist);
            if (dentist._id !== selectedDentist?._id || selectedClinic?._id !== newClinic._id) {
                // Number the dentist now, so the internal ID exists before the order is saved.
                const dentistLocalIds = await assignIdToDentist(dentist._id, newClinic._id);
                const numberedDentist = dentistLocalIds ? { ...dentist, dentistLocalIds } : dentist;
                setSelectedDentist(numberedDentist);
                setOrder((prev) => {
                    return {
                        ...prev,
                        ...{
                            dentist: numberedDentist as User,
                            deliveryDetails: {
                                ...prev.deliveryDetails,
                                pickUpLocation: {} as Organization,
                                destination: {} as Organization,
                            },
                        },
                    };
                });
            }
            // getClinic is the only writer of order.customer, and it is awaited here, so the
            // clinic is never left half-resolved for the construction step to read.
            await handleClinicChange(newClinic);
        }
    };

    const isSelectedDentist = (dentist: Dentist) => {
        const dentistId = dentist ? dentist._id : null;
        const selectedDentistId = selectedDentist ? selectedDentist._id : null;
        if (!dentistId && !selectedDentistId) {
            return false;
        }
        return dentistId === selectedDentistId;
    };

    const handleDeliveryClinicChange = (deliveryClinic: Organization, isUserSelection = false) => {
        setSelectedDeliveryClinic(deliveryClinic);
        setOrder((prev) => ({
            ...prev,
            deliveryDetails: {
                ...prev.deliveryDetails,
                pickUpLocation: deliveryClinic,
                destination: deliveryClinic,
            },
        }));
        if (shouldOfferToSaveAsDefault(deliveryClinic, isUserSelection)) {
            setClinicPendingSaveAsDefault(deliveryClinic);
        }
    };

    const onDentistCreation = async (dentist: User, clinic: Organization) => {
        if (clinic) {
            const dentistLocalIds = await assignIdToDentist(dentist._id, clinic._id);
            setOrder((prev) => ({
                ...prev,
                dentist: dentistLocalIds
                    ? {
                          ...dentist,
                          dentistLocalIds,
                      }
                    : {
                          ...dentist,
                      },
            }));
            // Same recompute every other clinic-selection path performs: writes order.customer
            // and resolves ORDER_VERSION into isActionBasedOrder plus the TioTandvard flag.
            await handleClinicChange(clinic);
        }
    };

    return (
        <Fragment>
            <Box mb={2}>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                        <Box mb={1}>
                            <DentistsWithClinicSearch dentist={selectedDentist} onSelect={handleDentistChange} />
                        </Box>
                        <Button
                            onClick={() => setAddDentistDialogOpen(true)}
                            startIcon={<AddIcon />}
                            data-test="dentists-with-clinic.add-dentist"
                        >
                            {translate('orders:ADD_DENTIST')}
                        </Button>
                    </Grid>
                    {selectedClinic && showDeliveryClinicDropdown() ? (
                        <Grid item xs={12} md={4}>
                            <DeliveryClinicAutocomplete
                                parentClinic={selectedClinic}
                                deliveryClinic={selectedDeliveryClinic}
                                preferredClinicId={labDefaultClinicId}
                                onDeliveryClinicChange={handleDeliveryClinicChange}
                            />
                        </Grid>
                    ) : undefined}
                </Grid>
            </Box>

            {addDentistDialogOpen && (
                <AddDentistToClinicDialog
                    open={addDentistDialogOpen}
                    onDialogClose={() => setAddDentistDialogOpen(false)}
                    onDentistCreation={onDentistCreation}
                />
            )}
            <ConfirmDialog
                open={!!clinicPendingSaveAsDefault}
                handleClose={() => setClinicPendingSaveAsDefault(null)}
                handleConfirm={handleSaveAsDefaultConfirm}
                dialogTitle={translate('orders:SAVE_DEFAULT_CLINIC_TITLE')}
                dialogContent={translate('orders:SAVE_DEFAULT_CLINIC_CONTENT', {
                    clinic: clinicPendingSaveAsDefault?.name || '',
                    dentist: selectedDentist?.fullName || '',
                })}
                confirmBtnLabel={translate('common:YES')}
                declineBtnLabel={translate('common:NO')}
            />
        </Fragment>
    );
};

export default DentistsWithClinic;
